import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const UpdateContentItemTitle = graphql(`
  mutation UpdateContentItemTitle($id: ContentItemId!, $title: String) {
    updateContentItem(input: { id: $id, title: $title }) {
      __typename
      ... on UpdateContentItemSuccess {
        contentItem {
          __typename
          id
          title
        }
      }
      ... on UpdateContentItemFailure {
        message
      }
    }
  }
`)

type UpdateTitleResult = { success: true } | { success: false; error: string }

/**
 * Set a ContentItem's `title` from the Composer header. The mutation response
 * writes `id`/`title` back into the normalized cache, so surfaces reading
 * `title` off the same ContentItem (e.g. drafts list cards) pick it up
 * without a refetch.
 */
export function useUpdateContentItemTitle(): (
  id: string,
  title: string | null,
) => Promise<UpdateTitleResult> {
  const [mutate] = useMutation(UpdateContentItemTitle)

  return useCallback(
    async (id, title) => {
      try {
        const { data } = await mutate({
          variables: { id, title },
          optimisticResponse: {
            updateContentItem: {
              __typename: 'UpdateContentItemSuccess',
              contentItem: {
                __typename: 'ContentItem',
                id,
                title,
              },
            },
          },
        })
        const payload = data?.updateContentItem
        if (payload?.__typename === 'UpdateContentItemSuccess') {
          return { success: true }
        }
        return {
          success: false,
          error:
            payload?.__typename === 'UpdateContentItemFailure'
              ? payload.message
              : 'Unknown error',
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      }
    },
    [mutate],
  )
}
